// Problem-based help articles (MARKETING.md §2.5) — target high-intent searches
// like "burst pipe what to do", each answering the question then routing to the
// booking flow. Rendered by HelpArticle.tsx with Article + FAQPage schema.

/** A plain string, or text that depends on whether the upfront paywall is on. */
export type ConditionalText = string | ((paywallEnabled: boolean) => string)

export function resolveText(text: ConditionalText, paywallEnabled: boolean): string {
  return typeof text === 'function' ? text(paywallEnabled) : text
}

export interface ArticleSection {
  h2: string
  paragraphs: ConditionalText[]
}

export interface HelpArticle {
  slug: string
  title: string
  metaTitle: string
  metaDescription: string
  updated: string // ISO date, shown + used for Article schema
  intro: string[]
  sections: ArticleSection[]
  faqs: { q: string; a: ConditionalText }[]
}

export const HELP_ARTICLES: HelpArticle[] = [
  {
    slug: 'burst-pipe-what-to-do',
    title: 'What to do when a pipe bursts — and how to get it fixed fast',
    metaTitle: 'Burst Pipe? What To Do Right Now (UK Emergency Guide)',
    metaDescription: 'A pipe has burst — here\'s exactly what to do in the first few minutes to limit the damage, and how to get an emergency plumber to you fast.',
    updated: '2026-07-03',
    intro: [
      'A burst pipe can flood a room in minutes, so the first job is to stop the water and limit the damage — then get a qualified plumber out fast. Here\'s exactly what to do, in order.',
    ],
    sections: [
      {
        h2: 'Stop the water immediately',
        paragraphs: [
          'Turn off your main stopcock. It\'s usually under the kitchen sink, in a downstairs cupboard, or where the mains pipe enters the house. Turn it clockwise until it stops. This cuts the water supply to the whole property.',
          'If the leak is from a hot water or heating pipe, also turn off your boiler and, if you have one, the water supply to your hot water cylinder.',
        ],
      },
      {
        h2: 'Drain the system and reduce damage',
        paragraphs: [
          'Open all your cold taps to drain the remaining water from the pipes quickly. Then flush the toilets to empty the cisterns.',
          'Turn off the electricity at the consumer unit if water is anywhere near sockets, wiring, or light fittings. Move furniture and valuables away from the leak and put down towels or buckets.',
        ],
      },
      {
        h2: 'Get an emergency plumber out',
        paragraphs: [
          'A burst pipe is a genuine emergency — you want a qualified plumber on site quickly. Rather than ringing round yourself, FixDelivered contacts nearby emergency plumbers for you, checks who\'s available and what they\'ll charge, and confirms one — usually within the hour.',
          (paywallEnabled: boolean) => paywallEnabled
            ? 'You\'ll see the plumber\'s callout fee upfront before anything is booked, and the booking fee is fully refunded if we can\'t find someone.'
            : 'We start contacting plumbers as soon as you submit your request, and right now the whole thing is completely free - no booking fee, at any point.',
        ],
      },
    ],
    faqs: [
      { q: 'Is a burst pipe an emergency?', a: 'Yes. Burst pipes cause fast, expensive water damage and can create electrical hazards. Turn off the mains stopcock straight away and get an emergency plumber out.' },
      { q: 'How much does it cost to fix a burst pipe?', a: 'An emergency callout typically runs £80–£180 depending on the time and access, plus parts. The plumber\'s fee is agreed with you upfront and paid on arrival.' },
      { q: 'Will my insurance cover a burst pipe?', a: 'Most home insurance covers the water damage from a burst pipe (and often the repair). Take photos before you clean up, and keep any receipts.' },
    ],
  },
  {
    slug: 'boiler-broken-down',
    title: 'Boiler broken down? What to do and how to get same-day help',
    metaTitle: 'Boiler Broken Down? What To Do + Same-Day Help (UK)',
    metaDescription: 'Boiler not working? Quick checks that might fix it, warning signs that need a plumber now, and how to get same-day emergency help.',
    updated: '2026-07-03',
    intro: [
      'A dead boiler means no heating and often no hot water. Before you book anyone, a few quick checks fix a surprising number of breakdowns — and if they don\'t, here\'s how to get help fast.',
    ],
    sections: [
      {
        h2: 'Quick checks to try first',
        paragraphs: [
          'Check the pressure gauge — if it\'s below 1 bar, the boiler may have locked out. Re-pressurising via the filling loop often brings it back (check your manual).',
          'Look for a fault or error code on the display and note it down. Check the thermostat is calling for heat, the boiler has power, and — if you\'re on a meter — that you\'re in credit for gas.',
          'If you smell gas, do not touch any switches. Leave the property and call the National Gas Emergency line on 0800 111 999.',
        ],
      },
      {
        h2: 'When to call a plumber right away',
        paragraphs: [
          'Call for help now if there\'s no hot water and no heating in cold weather, if the boiler is leaking, making banging noises, or repeatedly locking out, or if you have vulnerable people or young children in the home.',
          'Boiler work must be done by a Gas Safe registered engineer — never attempt gas repairs yourself.',
        ],
      },
      {
        h2: 'Get same-day help',
        paragraphs: [
          'FixDelivered rings qualified, Gas Safe registered plumbers near you, checks availability and callout fee, and confirms one — typically within the hour. You approve the fee before anything is booked.',
        ],
      },
    ],
    faqs: [
      { q: 'Why has my boiler stopped working?', a: 'Common causes are low pressure, a lockout after a fault, a tripped switch, or no gas supply. Check the pressure gauge and any error code first — many breakdowns are quick fixes.' },
      { q: 'How much does an emergency boiler repair cost?', a: 'An emergency callout is typically £80–£150 plus any parts. The engineer confirms the fee with you before starting.' },
      { q: 'Can I fix my boiler myself?', a: 'You can re-pressurise it and reset it per the manual, but any gas or internal repair must be done by a Gas Safe registered engineer.' },
    ],
  },
  {
    slug: 'no-hot-water',
    title: 'No hot water — is it an emergency and what should you do?',
    metaTitle: 'No Hot Water? Causes, Quick Fixes & When To Get Help',
    metaDescription: 'No hot water but heating still works — or nothing at all? The common causes, what you can check yourself, and how to get a plumber fast.',
    updated: '2026-07-03',
    intro: [
      'Losing hot water is disruptive but often fixable. What to check depends on whether your heating still works — that narrows down the cause quickly.',
    ],
    sections: [
      {
        h2: 'Heating works but no hot water',
        paragraphs: [
          'This usually points to a diverter valve (in a combi boiler) or a cylinder thermostat / immersion heater issue in a stored-hot-water system. Check the hot water is switched on at the programmer and the temperature is set high enough.',
          'If you have an immersion heater, check its switch and thermostat — a tripped immersion is a common and cheap fix.',
        ],
      },
      {
        h2: 'No hot water and no heating',
        paragraphs: [
          'That points to the boiler itself. Check the pressure gauge, any error code, the power supply, and your gas supply. See our boiler breakdown guide for the full checklist.',
        ],
      },
      {
        h2: 'Getting it sorted',
        paragraphs: [
          'If the basic checks don\'t restore hot water, a plumber can diagnose it quickly. FixDelivered finds and confirms a qualified local plumber for you — you\'ll see their callout fee upfront.',
        ],
      },
    ],
    faqs: [
      { q: 'Is no hot water an emergency?', a: 'It\'s not usually dangerous, but it\'s a priority — especially with children or vulnerable people. If it\'s paired with no heating in cold weather, treat it as urgent.' },
      { q: 'Why do I have heating but no hot water?', a: 'Most often a faulty diverter valve in a combi boiler, or a cylinder thermostat / immersion issue in a stored system.' },
      { q: 'How much to fix no hot water?', a: 'Diagnosis and minor repairs are typically £60–£150 plus parts; a diverter valve replacement can be more. The plumber confirms the cost before starting.' },
    ],
  },
  {
    slug: 'emergency-plumber-cost-uk',
    title: 'How much does an emergency plumber cost in the UK?',
    metaTitle: 'Emergency Plumber Cost UK (2026 Price Guide)',
    metaDescription: 'What an emergency plumber actually costs in the UK — callout fees, hourly rates, out-of-hours pricing, and how to avoid being overcharged.',
    updated: '2026-07-03',
    intro: [
      'Emergency plumber pricing varies by time, location and job, but there are clear typical ranges. Here\'s what to expect so you\'re not caught out.',
    ],
    sections: [
      {
        h2: 'Typical callout and hourly rates',
        paragraphs: [
          'Most UK emergency plumbers charge a callout fee of roughly £60–£150, which often covers the first hour. After that, hourly rates are commonly £40–£80.',
          'Evenings, weekends and bank holidays cost more — sometimes 1.5× to 2× the daytime rate. Parts are charged on top.',
        ],
      },
      {
        h2: 'What drives the price',
        paragraphs: [
          'The main factors are the time of day, how urgent and complex the job is, your location, and the parts required. A simple leak isolation is far cheaper than replacing a section of burst pipe or a boiler component.',
        ],
      },
      {
        h2: 'How to avoid being overcharged',
        paragraphs: [
          (paywallEnabled: boolean) => paywallEnabled
            ? 'Always agree the callout fee and rate before the plumber starts, and ask for the likely total. With FixDelivered you see each plumber\'s callout fee upfront and confirm it before anything is booked — and our booking fee is refunded if we can\'t find you someone.'
            : 'Always agree the callout fee and rate before the plumber starts, and ask for the likely total. With FixDelivered you see each plumber\'s callout fee upfront, and right now there\'s no booking fee to us at all — it\'s completely free.',
        ],
      },
    ],
    faqs: [
      { q: 'How much is an emergency plumber callout?', a: 'Typically £60–£150 in the UK, often including the first hour. Out-of-hours callouts cost more.' },
      { q: 'Are emergency plumbers more expensive at night?', a: 'Yes — evening, weekend and bank-holiday rates are commonly 1.5×–2× the standard daytime rate.' },
      { q: 'Do I pay the plumber or FixDelivered?', a: (paywallEnabled: boolean) => paywallEnabled
        ? 'You pay a small flat booking fee to FixDelivered to find and confirm a plumber; the plumber\'s callout fee is paid directly to them on arrival.'
        : 'Right now, nothing to FixDelivered - the service is completely free, no booking fee. The plumber\'s callout fee is paid directly to them on arrival.' },
    ],
  },
  {
    slug: 'blocked-drain-emergency',
    title: 'Is a blocked drain an emergency? What to do right now',
    metaTitle: 'Blocked Drain Emergency? What To Do (UK Guide)',
    metaDescription: 'When a blocked drain is an emergency, what you can try yourself, and how to get a plumber out fast if water is backing up.',
    updated: '2026-07-03',
    intro: [
      'A slow drain is annoying; a fully blocked one that\'s backing up sewage or flooding is an emergency. Here\'s how to tell the difference and what to do.',
    ],
    sections: [
      {
        h2: 'When it\'s an emergency',
        paragraphs: [
          'Treat it as urgent if wastewater or sewage is backing up into sinks, baths or toilets, if multiple drains are blocked at once (a sign of a main drain problem), or if there\'s a risk of flooding inside the property.',
          'Stop using water in the affected part of the house to avoid making the backup worse.',
        ],
      },
      {
        h2: 'What you can try',
        paragraphs: [
          'For a single slow sink or toilet, a plunger or drain rods sometimes clear it. Avoid pouring caustic chemicals down repeatedly — they can damage pipes and rarely fix a serious blockage.',
          'If it\'s more than one drain, or water is rising, don\'t keep trying — you likely have a blockage in the main drain that needs professional equipment.',
        ],
      },
      {
        h2: 'Get help fast',
        paragraphs: [
          'FixDelivered finds a qualified local plumber who can clear the blockage, checks their fee and arrival time, and confirms one for you — with the callout fee shown upfront.',
        ],
      },
    ],
    faqs: [
      { q: 'Is a blocked drain an emergency?', a: 'It is if sewage is backing up, multiple drains are blocked, or there\'s a flood risk. A single slow drain usually isn\'t urgent.' },
      { q: 'How much to unblock a drain?', a: 'A straightforward unblock is typically £80–£150; jetting or a CCTV survey for a main-drain blockage costs more. Agree the price before work starts.' },
      { q: 'Should I use chemical drain cleaner?', a: 'Sparingly at most. Repeated use can damage pipes and rarely clears a serious blockage — rodding or jetting is more effective and safer.' },
    ],
  },
  {
    slug: 'heating-not-working',
    title: 'Heating not working — emergency or not? How to get help fast',
    metaTitle: 'Heating Not Working? Quick Fixes & Emergency Help (UK)',
    metaDescription: 'No heating? Fast checks that often fix it, when it becomes an emergency, and how to get a Gas Safe plumber out quickly.',
    updated: '2026-07-03',
    intro: [
      'Losing heating is miserable in cold weather, but many cases come down to a handful of quick checks. Here\'s what to try, and when to get a plumber.',
    ],
    sections: [
      {
        h2: 'Fast checks',
        paragraphs: [
          'Check the thermostat is set above room temperature and, if it\'s battery powered, that the batteries aren\'t flat. Check the programmer/timer is set to come on and the boiler has power.',
          'Check the boiler pressure (ideally around 1–1.5 bar) and look for an error code. Bleeding radiators can help if some are cold at the top while the boiler runs fine.',
        ],
      },
      {
        h2: 'When it\'s an emergency',
        paragraphs: [
          'No heating becomes urgent in very cold weather, or where there are elderly residents, young children, or anyone unwell. A leaking boiler or a gas smell is always an emergency — for a gas smell, leave and call 0800 111 999.',
        ],
      },
      {
        h2: 'Getting a plumber',
        paragraphs: [
          'Heating and boiler faults need a Gas Safe registered engineer. FixDelivered contacts qualified local engineers, confirms one who can attend, and shows you the callout fee before you commit.',
        ],
      },
    ],
    faqs: [
      { q: 'Why is my heating not working but I have hot water?', a: 'Often a thermostat, programmer, or diverter-valve issue, or a pump/motorised valve fault. Check the thermostat and timer first.' },
      { q: 'Is no heating an emergency?', a: 'It can be — in cold weather or where vulnerable people live. A leaking boiler or gas smell is always an emergency.' },
      { q: 'Who fixes central heating?', a: 'A Gas Safe registered heating engineer. FixDelivered finds and confirms one near you, with the callout fee shown upfront.' },
    ],
  },
]

export const ARTICLE_BY_SLUG: Record<string, HelpArticle> = Object.fromEntries(HELP_ARTICLES.map(a => [a.slug, a]))
